"use client";

// Extracted from app/page.tsx lines 506–560.
// Added collapsible menu: single avatar button opens animated dropdown with
// Studio, Notifications, Profile, and Logout items.

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { Upload, User as UserIcon, LogOut, X, LayoutGrid } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { UserResponse } from "@/api-client";
import { NotificationBell } from "@/shared/ui";
import { MapStyleKey } from "@/features/map/mapTheme";
import { useTheme } from "@/features/theme";

interface TopNavBarProps {
  user: UserResponse | null;
  isAuthLoading: boolean;
  isGlowing: boolean;
  menuOpen: boolean;
  onMenuOpenChange: (open: boolean) => void;
  onUpload: () => void;
  onShowAuth: () => void;
  onLogout: () => void;
  mapStyle?: MapStyleKey;
}

const NAV_BASE_SHADOW = "rgba(0, 0, 0, 0.25) 0px 25px 50px -12px, rgba(255, 255, 255, 0.6) 0px 0px 0px 1.5px";
const NAV_GLOW_LOW = `rgba(0, 0, 0, 0.25) 0px 25px 50px -12px, rgba(255, 255, 255, 0.6) 0px 0px 0px 1.5px, 0 0 14px 4px rgba(255,255,255,0.06)`;
const NAV_GLOW_HIGH = `rgba(0, 0, 0, 0.25) 0px 25px 50px -12px, rgba(255, 255, 255, 0.8) 0px 0px 0px 1.5px, 0 0 22px 6px rgba(255,255,255,0.12)`;

const dropdownVariants = {
  hidden: { opacity: 0, scale: 0.94, y: -10 },
  visible: {
    opacity: 1, scale: 1, y: 0,
    transition: { duration: 0.22, ease: [0.16, 1, 0.3, 1] as [number, number, number, number] },
  },
  exit: {
    opacity: 0, scale: 0.94, y: -10,
    transition: { duration: 0.16, ease: "easeIn" as const },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: -6 },
  visible: (i: number) => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.055, duration: 0.18, ease: "easeOut" as const },
  }),
  exit: { opacity: 0, y: -6, transition: { duration: 0.12 } },
};

export default function TopNavBar({
  user, isAuthLoading, isGlowing, menuOpen, onMenuOpenChange, onUpload, onShowAuth, onLogout, mapStyle = "dark",
}: TopNavBarProps) {
  const { isDark } = useTheme();
  const [unreadCount, setUnreadCount] = useState(0);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onMenuOpenChange(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [menuOpen, onMenuOpenChange]);

  return (
    <header className="absolute top-0 left-0 right-0 z-[40] p-4 sm:p-8 flex justify-between items-start pointer-events-none transition-opacity duration-700">
      {/* Brand */}
      <div className="pointer-events-auto">
        <img src="/logo.png" alt="360 Maps" className="h-8 sm:h-10 md:h-12 drop-shadow-sm" />

      </div>

      {/* Right side */}
      <div className="pointer-events-auto">
        {isAuthLoading ? (
          <div className="w-10 h-10 rounded-full bg-ink/10 animate-pulse border border-ink/5" />
        ) : user ? (
          <div className="relative" ref={menuRef}>
            {/* ── Toggle button ── */}
            <motion.button
              data-tour-id="nav-avatar"
              onClick={() => onMenuOpenChange(!menuOpen)}
              animate={{
                boxShadow: isGlowing
                  ? [NAV_GLOW_LOW, NAV_GLOW_HIGH, NAV_GLOW_LOW]
                  : NAV_BASE_SHADOW,
              }}
              transition={{
                boxShadow: isGlowing
                  ? { repeat: Infinity, duration: 1.5, ease: "easeInOut" }
                  : { duration: 1, ease: "easeOut" },
              }}
              className="relative flex items-center justify-center w-11 h-11 rounded-full bg-tone-800 backdrop-blur-md hover:bg-tone-700 transition-colors focus:outline-none"
              title="Menu"
              aria-expanded={menuOpen}
              aria-haspopup="true"
            >
              {/* Avatar or fallback icon */}
              <div className="absolute inset-0 rounded-full overflow-hidden">
                {user.avatar_url ? (
                  <img
                    src={user.avatar_url}
                    alt={user.username}
                    className="w-full h-full object-cover opacity-80"
                  />
                ) : null}
              </div>

              {/* Overlay icon that transitions between open/close */}
              <AnimatePresence mode="wait" initial={false}>
                {menuOpen ? (
                  <motion.span
                    key="close"
                    className="relative z-10 flex items-center justify-center w-full h-full rounded-full bg-canvas/60"
                    initial={{ rotate: -90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: 90, opacity: 0 }}
                    transition={{ duration: 0.15 }}
                  >
                    <X className="w-4 h-4 text-ink" />
                  </motion.span>
                ) : (
                  <motion.span
                    key="open"
                    className={`relative z-10 flex items-center justify-center w-full h-full rounded-full ${user.avatar_url ? "bg-canvas/30" : ""}`}
                    initial={{ rotate: 90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: -90, opacity: 0 }}
                    transition={{ duration: 0.15 }}
                  >
                    {!user.avatar_url && <LayoutGrid className="w-4 h-4 text-ink/70" />}
                  </motion.span>
                )}
              </AnimatePresence>

              {/* Unread notification bubble */}
              <AnimatePresence>
                {unreadCount > 0 && !menuOpen && (
                  <motion.span
                    key="unread-badge"
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0, opacity: 0 }}
                    transition={{ type: "spring", stiffness: 400, damping: 20 }}
                    className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-0.5 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center leading-none z-20 shadow-lg"
                  >
                    {unreadCount > 9 ? "9+" : unreadCount}
                  </motion.span>
                )}
              </AnimatePresence>
            </motion.button>

            {/* ── Dropdown ── */}
            <AnimatePresence>
              {menuOpen && (
                <motion.div
                  key="nav-dropdown"
                  variants={dropdownVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  style={{ transformOrigin: "top right" }}
                  className={`absolute right-0 top-full mt-2.5 w-56 rounded-2xl shadow-2xl overflow-hidden z-[200] p-1.5 flex flex-col gap-0.5 ${isDark ? "bg-neutral-950/80 backdrop-blur-xl border border-white/10 shadow-black/70" : "bg-white border border-black/[0.08] shadow-black/10"}`}
                >
                  {/* 1. Studio (Upload) — unique gradient style */}
                  <motion.div custom={0} variants={itemVariants} initial="hidden" animate="visible" exit="exit">
                    <button
                      onClick={() => { onMenuOpenChange(false); onUpload(); }}
                      className="relative w-full flex items-center gap-3 px-4 py-3 rounded-xl overflow-hidden group focus:outline-none"
                    >
                      {/* Gradient background */}
                      <span className="absolute inset-0 bg-gradient-to-r from-violet-600 via-blue-600 to-indigo-500 opacity-90 group-hover:opacity-100 transition-opacity" />
                      {/* Glow pulse */}
                      <span className="absolute inset-0 rounded-xl ring-1 ring-ink/20 group-hover:ring-ink/30 transition-all" />
                      <Upload className="relative z-10 w-4 h-4 text-white shrink-0 group-hover:scale-110 transition-transform" />
                      <span className="relative z-10 text-white font-black uppercase tracking-widest text-xs">
                        Studio
                      </span>
                      <span className="relative z-10 ml-auto text-[9px] text-white/50 font-light normal-case tracking-normal">
                        upload
                      </span>
                    </button>
                  </motion.div>

                  <div className={`h-px mx-2 my-0.5 bg-ink/5`} />

                  {/* 2. Notifications */}
                  <motion.div
                    custom={1} variants={itemVariants} initial="hidden" animate="visible" exit="exit"
                    className={`flex items-center gap-3 px-4 py-2 rounded-xl transition-colors cursor-default hover:bg-ink/5`}
                  >
                    <div className="-ml-2 shrink-0">
                      <NotificationBell onUnreadChange={setUnreadCount} />
                    </div>
                    <span className={`text-sm flex-1 select-none text-tone-300`}>Notifications</span>
                  </motion.div>

                  {/* 3. Profile */}
                  <motion.div custom={2} variants={itemVariants} initial="hidden" animate="visible" exit="exit">
                    <Link
                      href={`/creators/${encodeURIComponent(user.username)}`}
                      onClick={() => onMenuOpenChange(false)}
                      className={`flex items-center gap-3 px-4 py-2 rounded-xl transition-colors hover:bg-ink/5`}
                    >
                      <div className="-ml-2 shrink-0">
                        <div className="w-6 h-6 rounded-full bg-tone-800 border border-tone-700 flex items-center justify-center overflow-hidden">
                          {user.avatar_url ? (
                            <img src={user.avatar_url} alt={user.username} className="w-full h-full object-cover" />
                          ) : (
                            <UserIcon className="w-3 h-3 text-tone-400" />
                          )}
                        </div>
                      </div>
                      <span className={`text-sm flex-1 text-tone-300`}>Profile</span>
                      <span className={`text-xs truncate max-w-[72px] text-tone-500`}>{user.username}</span>
                    </Link>
                  </motion.div>

                  {/* 4. Logout */}
                  <motion.div custom={3} variants={itemVariants} initial="hidden" animate="visible" exit="exit">
                    <button
                      onClick={() => { onMenuOpenChange(false); onLogout(); }}
                      className="w-full flex items-center gap-3 px-4 py-2 rounded-xl hover:bg-red-500/10 transition-colors group focus:outline-none"
                    >
                      <LogOut className={`w-4 h-4 group-hover:text-red-400 group-hover:-translate-x-0.5 transition-all shrink-0 text-tone-400`} />
                      <span className={`text-sm group-hover:text-red-400 transition-colors text-tone-300`}>Logout</span>
                    </button>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        ) : (
          <button
            onClick={onShowAuth}
            className="flex items-center gap-2 px-4 py-2.5 sm:px-8 sm:py-3 bg-tone-800 text-ink text-xs font-black uppercase tracking-widest rounded-full hover:bg-tone-700 active:scale-95 transition-all min-h-[44px]"
            style={{ boxShadow: NAV_BASE_SHADOW }}
          >
            Sign In
          </button>
        )}
      </div>
    </header>
  );
}
